import { parseISO, isValid } from 'date-fns';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const createUrl = (
  pathname: string,
  params: URLSearchParams,
) => {
  const paramsString = params.toString();
  const queryString = `${paramsString.length ? '?' : ''}${paramsString}`;
  return `${pathname}${queryString}`;
};

export function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    if ('name' in error && (error as Error).name === 'AccessDeniedException') {
      return 'Access is denied. You do not have the required permissions.';
    }
    if ('message' in error) {
      return String((error as any).message);
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An error occurred';
}

export const getQueryParams = (url: string) => {
  return Object.fromEntries(new URL(url).searchParams);
};

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {},
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === 'accurate'
      ? (accurateSizes[i] ?? 'Bytest')
      : (sizes[i] ?? 'Bytes')
  }`;
}

export function toPascalCase(str: string) {
  return str
    .replace(/[_\-\s]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

export function toSnakeCase(str: string) {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s\\-]+/g, '_')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

export function toCapitalizeCase(str: string, separator: '_' | '-' = '_') {
  return str
    .split(separator)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const getKeyByValue = <T extends Record<string, string>>(
  obj: T,
  value: string,
): keyof T | undefined => {
  return (Object.keys(obj) as Array<keyof T>).find(key => obj[key] === value);
};

export function toCamelCase(snakeCaseString: string): string {
  return snakeCaseString
    .replace(/_([a-z])/g, (_, char) => char.toUpperCase())
    .replace(/[^a-zA-Z0-9 ]/g, '');
}

export function removeSpecialCharsAndSpaces(str: string): string {
  return str.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '');
}

export function mapSqlDataTypeToReadable(sqlDataType: string): string {
  const lowerCaseType = sqlDataType.toLowerCase();

  switch (lowerCaseType) {
    case 'varchar':
    case 'varchar(255)':
    case 'varchar(5)':
      return 'Short Text';
    case 'text':
      return 'Long Text';
    case 'int':
    case 'bigint':
    case 'smallint':
      return 'Whole Number';
    case 'decimal':
    case 'decimal(10, 2)':
      return 'Decimal Number';
    case 'date':
      return 'Date';
    case 'datetime':
    case 'timestamp':
      return 'Date and Time';
    case 'boolean':
      return 'Yes/No (Boolean)';
    default:
      return sqlDataType.charAt(0).toUpperCase() + sqlDataType.slice(1);
  }
}

export const parseNullableFlag = (flag: 'YES' | 'NO'): boolean =>
  flag === 'YES';

const iso8601Regex = new RegExp(
  /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})(?:\.([0-9]+))?(Z|([+-])([0-9]{2}):([0-9]{2}))?$/,
);

const iso8601DateOnlyRegex = new RegExp(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);

export function isValidStrictISOString(dateString: string) {
  if (typeof dateString !== 'string') {
    return false;
  }
  const isStructuralMatch =
    iso8601Regex.test(dateString) || iso8601DateOnlyRegex.test(dateString);
  if (!isStructuralMatch) {
    return false;
  }
  const parsedDate = parseISO(dateString);
  return isValid(parsedDate);
}

export function transformDateFormat(dateString: string) {
  const regex = /^\d{1,2}-\d{4}$/;
  if (regex.test(dateString)) {
    const parts = dateString.split('-');
    const month = parts[0];
    const year = parts[1];
    const paddedMonth = month.padStart(2, '0');
    return `${year}-${paddedMonth}-01`;
  } else {
    return dateString;
  }
}
